import { CancelCircle } from '@squareup/dex-icons/market/alert';
import { AnchoredHeading, IconButton } from '@squareup/dex-ui';
import { Box, Paragraph30 } from '@squareup/dex-ui-shared-base';
import { commonIconStyles } from '@squareup/dex-ui-shared-icon-styles';
import React, { FC, ReactNode, useCallback } from 'react';

import { ApiExplorerFieldProps } from '../fields/props';

import styles from './api-explorer-field-layout.module.css';

const strings = {
  bodyHeader: 'Body',
  queryPathParams: 'Parameters',
  authorizationHeader: 'Authorization',
  bodyClearButtonLabel: 'Clear body parameters',
  pathClearButtonLabel: 'Clear parameters',
  authClearButtonLabel: 'Clear authorization parameters',
  clearFieldsTooltip: 'Clear fields',
};

interface ClearButtonProps {
  onClick: () => void;
  label: string;
  testId: string;
}

const ClearButton: FC<ClearButtonProps> = ({ onClick, label, testId }) => {
  return (
    <IconButton
      containerClassName={styles['cancel-icon']}
      colorVariant="20"
      width={16}
      height={16}
      testId={testId}
      tooltipText={strings.clearFieldsTooltip}
      aria-label={label}
      icon={
        <CancelCircle className={commonIconStyles['icon-fill-current-color']} />
      }
      trackingId="property-info-close-button"
      onClick={onClick}
    />
  );
};

interface Props {
  onChange: ApiExplorerFieldProps['onChange'];
  queryPathParamFields?: ReactNode | undefined;
  bodyFields?: ReactNode | undefined;
  authParamField?: ReactNode | undefined;
}

const ApiExplorerFieldLayout: FC<Props> = ({
  onChange,
  queryPathParamFields,
  authParamField,
  bodyFields,
}) => {
  const onClearPathQueryParamsClicked = useCallback(() => {
    onChange({}, '/pathParameters', 'replace');
    onChange({}, '/queryParameters', 'replace');
  }, [onChange]);

  const onClearBodyParamsClicked = useCallback(() => {
    onChange({}, '/bodyParameters', 'replace');
  }, [onChange]);

  const onClearAuthParamsClicked = useCallback(() => {
    onChange({}, '/authParameters', 'replace');
  }, [onChange]);

  return (
    <>
      {authParamField && (
        <>
          <Box
            padding={{ bottom: '1x', top: '3x' }}
            border={{ line: { bottom: 'standard' } }}
          >
            <AnchoredHeading
              anchor="authorization"
              trackingId="api-explorer-auth-anchor"
              className={styles['anchored-heading']}
            >
              <Paragraph30 as="h2" weight="semi-bold">
                {strings.authorizationHeader}
              </Paragraph30>
              <ClearButton
                label={strings.authClearButtonLabel}
                onClick={onClearAuthParamsClicked}
                testId="auth-clear-parameters-button"
              />
            </AnchoredHeading>
          </Box>
          {authParamField}
        </>
      )}
      {queryPathParamFields && (
        <>
          <Box
            padding={{ bottom: '1x', top: '3x' }}
            border={{ line: { bottom: 'standard' } }}
          >
            <AnchoredHeading
              anchor="parameters"
              trackingId="api-explorer-parameter-anchor"
              className={styles['anchored-heading']}
            >
              <Paragraph30 as="h2" weight="semi-bold">
                {strings.queryPathParams}
              </Paragraph30>
              <ClearButton
                label={strings.pathClearButtonLabel}
                onClick={onClearPathQueryParamsClicked}
                testId="param-clear-parameters-button"
              />
            </AnchoredHeading>
          </Box>
          {queryPathParamFields}
        </>
      )}
      {bodyFields && (
        <>
          <Box
            padding={{ bottom: '1x', top: '3x' }}
            border={{ line: { bottom: 'standard' } }}
          >
            <AnchoredHeading
              anchor="body"
              trackingId="api-explorer-body-anchor"
              className={styles['anchored-heading']}
            >
              <Paragraph30 as="h2" weight="semi-bold">
                {strings.bodyHeader}
              </Paragraph30>
              <ClearButton
                label={strings.bodyClearButtonLabel}
                onClick={onClearBodyParamsClicked}
                testId="body-clear-parameters-button"
              />
            </AnchoredHeading>
          </Box>
          {bodyFields}
        </>
      )}
      <Box margin={{ bottom: '3x' }} />
    </>
  );
};

export { ApiExplorerFieldLayout };
